import { Component, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormControl, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';

import { User } from '@shared/models/user.model';
import { UserService } from '@shared/services/user.service';
import { TakeUntilDestroy } from '@shared/decorators/take-until-destroy.decorator';
import { LoggerService } from '@app/shared/services/logger.service';
import { EventManager } from '@app/shared/utils/event-manager';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { Observable } from 'rxjs';
import { UserRepository } from '@app/shared/state/user/user.repository';
import { ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from '@app/shared/shared.module';
import { Heading2Component } from '@app/shared/components/text/h2/h2.component';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { ParagraphComponent } from '@app/shared/components/text/p/p.component';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-manage-name',
  templateUrl: './manage-name.component.html',
  styleUrls: ['./manage-name.component.scss'],
  standalone: true,
  imports: [SharedModule, ReactiveFormsModule, Heading2Component, ButtonComponent, ParagraphComponent],
})
export class ManageNameComponent {
  hideNew = true;
  hideCurrent = true;
  componentDestroy: () => Observable<unknown>;
  current = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(6),
    Validators.maxLength(16),
    Validators.pattern(/^\S*$/),
  ]);
  readonly currentUser: Signal<User | undefined>;

  private readonly userService = inject(UserService);
  public readonly logger = inject(LoggerService);
  private readonly eventManager = inject(EventManager);
  private readonly userRepo = inject(UserRepository);

  constructor() {
    this.currentUser = toSignal(this.userService.currentUser$);
  }

  onSubmit(): void {
    const user = this.currentUser()!;
    this.userService
      .updateNickName(user.id, {
        CurrentNickName: user.nickName,
        NewNickName: this.current.value,
      })
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        next: () => {
          user.nickName = this.current.value;
          user.canChangeName = false;
          this.eventManager.closeModal(new Modal(NonPersistedModalName.ManageNameComponent, ModalType.View));
          this.userService.setCurrentUser(user);
          this.userRepo.updateUser(user);
        },
        error: (error) => {
          this.logger.error(error);
          if (error.status === 409) {
            this.current.setErrors({ sameUserName: true });
          }
          if (error.status === 406 || error.status === 400) {
            this.current.setErrors({ emojis: true });
          }
          if (error.status === 412) {
            this.current.setErrors({ userExist: true });
          }
        },
      });
  }

  close() {
    this.eventManager.closeModal(new Modal(NonPersistedModalName.ManageNameComponent, ModalType.View));
  }
}
